import React, { useState } from 'react';
import {
  Badge, Box, Button, HStack, Stack, Text, useToast, useColorModeValue,
} from '@chakra-ui/react';
import { FiDownload, FiEye, FiRotateCcw } from 'react-icons/fi';
import { NIL } from 'uuid';
import IconProposals from '../assets/Icon-Proposals.svg';
import DocumentGridShell from './DocumentGridShell';
import {
  CONTRACT_STATUS_LABEL, CONTRACT_TYPE_LABEL, ContractDocumentRow, ContractStatus,
} from '../models/contract';
import {
  useProjectContractsArchived, useCustomerContractsArchived, useRestoreContract, openContractPdf, downloadContractPdf,
} from '../hooks/useContracts';
import useCurrentUser from '../hooks/useCurrentUser';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';

// item 1426 (Chad: "tell me where my archived contracts go" → nowhere visible; "do that") —
// the ARCHIVED CONTRACTS area. Archive (item 906) only flips IsArchive and every contracts list
// filtered it out, so an archived paper was reachable through the database alone. This mirrors
// ArchivedProposalsSection: hidden when empty, read-only rows (Preview/Download), Restore in the
// primary-action slot, super-admin gated (the same rule as Archive — item 142).
const statusScheme = (status: ContractStatus): string => {
  if (status === ContractStatus.Executed) return 'green';
  if (status === ContractStatus.Finalized) return 'purple';
  return 'gray';
};

const ArchivedContractsSection: React.FC<{
  projectId?: string;
  customerId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}> = ({ projectId, customerId, collapse }) => {
  const project = useProjectContractsArchived(projectId && projectId !== NIL ? projectId : undefined);
  const customer = useCustomerContractsArchived(customerId && customerId !== NIL ? customerId : undefined);
  const query = projectId ? project : customer;
  const { isSuperAdmin } = useCurrentUser();
  const restorer = useRestoreContract();
  const toast = useToast();
  const rowFill = useColorModeValue('blackAlpha.50', 'whiteAlpha.50'); // item 958 — light-mode contrast (row tint vanished on light)
  // item 1437 — ALWAYS a collapsible section: the standalone (non-collapsible) DocumentGridShell
  // reserves ~100vh of min-height (item 1063) and pulls its margins negative — fine for a page,
  // a giant hole when embedded on the project page / contact form. Callers that don't manage the
  // collapse state get a local one.
  const [localCollapsed, setLocalCollapsed] = useState(false);
  const fold = collapse ?? { collapsed: localCollapsed, onToggle: () => setLocalCollapsed((v) => !v) };

  const rows: ContractDocumentRow[] = query.data?.index ?? [];
  // an empty Archived section would only clutter the page (item 553's rule for proposals)
  if (query.status !== 'success' || rows.length === 0) return null;

  const doRestore = (c: ContractDocumentRow) => restorer.mutate(c.id, {
    onSuccess: () => { toast({ status: 'success', title: `Restored ${c.title}.` }); },
    onError: (e: any) => {
      toast({
        status: 'error',
        title: 'Could not restore',
        description: e?.response?.status === 403 ? 'Only a Pulse super-admin can restore contracts.' : String(e?.response?.data ?? 'Try again.'),
      });
    },
  });

  return (
    <DocumentGridShell
      heading="ARCHIVED CONTRACTS"
      icon={IconProposals}
      collapsible
      collapsed={fold.collapsed}
      onToggleCollapse={fold.onToggle}
      status="success"
      data={rows}
    >
      <Stack spacing={2}>
        {rows.map((c) => (
          <Box key={c.id} px={3} py={2} borderRadius="md" bg={rowFill} opacity={0.85}>
            <HStack spacing={4} wrap="wrap">
              <Box minW={0}>
                <Text fontWeight="semibold" noOfLines={1}>{c.title}</Text>
                <Text fontSize="xs" color="gray.500">
                  {`${CONTRACT_TYPE_LABEL[c.type]}${c.number ? ` · ${c.number}` : ''} · ${SqlDateTimeToDisplay(c.effectiveDate)}`}
                </Text>
              </Box>
              <Badge colorScheme={statusScheme(c.status)}>{CONTRACT_STATUS_LABEL[c.status]}</Badge>
              <Badge variant="outline" colorScheme="gray">ARCHIVED</Badge>
              <HStack ml="auto" spacing={1} flexWrap="wrap" justify="flex-end" sx={{ rowGap: '4px' }}>
                <Button size="xs" variant="ghost" leftIcon={<FiEye />} onClick={() => openContractPdf(c.id).catch(() => toast({ status: 'error', title: 'Could not open the PDF.' }))}>Preview</Button>
                <Button size="xs" variant="ghost" leftIcon={<FiDownload />} onClick={() => downloadContractPdf(c.id, c.title || 'contract').catch(() => toast({ status: 'error', title: 'Could not download the PDF.' }))}>Download</Button>
                {isSuperAdmin && (
                  <Button size="xs" colorScheme="blue" leftIcon={<FiRotateCcw />} isLoading={restorer.isLoading && restorer.variables === c.id} onClick={() => doRestore(c)}>
                    Restore
                  </Button>
                )}
              </HStack>
            </HStack>
          </Box>
        ))}
      </Stack>
    </DocumentGridShell>
  );
};

ArchivedContractsSection.defaultProps = { projectId: undefined, customerId: undefined, collapse: undefined };

export default ArchivedContractsSection;
